import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { debounce } from '../../../../utils/debounce';
import { ensureArray } from '../../../../utils/ensureArray';
import { Colors } from '../../../../theme';
const Styles = labelStyles('ScrollSnap', {
    root: () => [
        css({
            width: '100%',
            overflow: 'hidden',
        }),
    ],
    scrollableArea: () => [
        css({
            overflowX: 'auto',
            overscrollBehaviorX: 'none',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            scrollbarWidth: 'none',
            '&:focus-visible': {
                outline: `2px solid ${Colors.blue}`,
                outlineOffset: '-2px',
            },
        }),
    ],
    scrollingSurface: (props, pageCount, alignment) => [
        css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: pageCount * props.itemWidth + props.gap * Math.max(pageCount - 1, 0),
        }),
        alignment === 'center' &&
            withModifier('centerAligned', {
                margin: '0 auto',
            }),
    ],
    snappable: (props) => [
        css({
            scrollSnapAlign: props.scrollSnapAlign,
            width: props.itemWidth,
        }),
    ],
});
const getSnapPoints = (opts) => {
    const { itemWidth, pageCount, scrollSnapAlign, gap } = opts;
    const snapPointGetter = {
        start: (_v, i) => (itemWidth + gap) * i,
        center: (_v, i) => itemWidth / 2 + i * (itemWidth + gap / 2),
        end: (_v, i) => itemWidth + i * (itemWidth + gap),
    }[scrollSnapAlign];
    return Array.from({ length: pageCount }).map(snapPointGetter);
};
const getSnapThresholds = (opts) => {
    const { itemWidth, scrollSnapAlign, pageCount, gap, clientWidth } = opts;
    const snappingPoints = getSnapPoints(opts);
    const width = itemWidth + gap;
    const stoppingPoint = width * pageCount - clientWidth;
    const snapThresholdGetter = {
        start: (acc, _v, i, src) => {
            let point;
            if (i === 0) {
                point = 0;
            }
            else if (i === 1) {
                point = width / 2;
            }
            else if (i === src.length - 1) {
                point = width * src.length - (clientWidth - width);
            }
            else {
                point = acc[i - 1] + width;
            }
            if (point >= stoppingPoint) {
                const offset = Math.max((stoppingPoint - snappingPoints[i - 1]) / 2, 1);
                point = stoppingPoint - offset;
            }
            acc[i] = point;
            return acc;
        },
        center: (acc, _v, i, src) => {
            if (i === 0) {
                acc[i] = 0;
            }
            else if (i === 1) {
                acc[i] = width + width / 4;
            }
            else if (i === src.length - 1) {
                acc[i] = width * src.length - width * 1.25;
            }
            else {
                acc[i] = width * i;
            }
            return acc;
        },
        end: (acc, _v, i) => {
            if (i === 0) {
                acc[i] = 0;
            }
            else if (i === 1) {
                acc[i] = width * 1.75;
            }
            else {
                acc[i] = i * width + width / 2;
            }
            return acc;
        },
    }[scrollSnapAlign];
    return Array.from({ length: pageCount }).reduce(snapThresholdGetter, []);
};
const DEBOUNCE_MS = 50;
const ScrollSnap = ({ children, itemWidth, alignment = 'left', gap = 0, onScroll = undefined, pageIndex = 0, scrollSnapAlign = 'start', }) => {
    var _a;
    const childArray = ensureArray(children);
    const pageCount = childArray.length;
    const rootRef = useRef(null);
    const clientWidth = ((_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || itemWidth;
    const scrollableAreaRef = useRef(null);
    const [currentPageIndex, setCurrentPageIndex] = useState(pageIndex);
    const snapThresholds = getSnapThresholds({
        pageCount,
        itemWidth,
        scrollSnapAlign,
        gap,
        clientWidth,
    });
    const updateScrollableAreaDebounced = useCallback(debounce((opts) => {
        var _a, _b;
        if (!opts || !opts.scrollableAreaRef.current || !opts.rootRef.current) {
            return;
        }
        const scrollLeftOffset = {
            start: 0,
            center: (((_a = opts.rootRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0) / 2,
            end: ((_b = opts.rootRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) || 0,
        }[opts.scrollSnapAlign];
        const snapPoints = getSnapPoints({
            pageCount: opts.pageCount,
            itemWidth: opts.itemWidth,
            scrollSnapAlign: opts.scrollSnapAlign,
            gap,
        });
        if (opts.scrollableAreaRef.current) {
            opts.scrollableAreaRef.current.scrollLeft =
                snapPoints[opts.pageIndex] - scrollLeftOffset;
        }
    }, DEBOUNCE_MS), []);
    useEffect(() => {
        return updateScrollableAreaDebounced({
            itemWidth,
            pageCount,
            pageIndex,
            rootRef,
            scrollableAreaRef,
            scrollSnapAlign,
        });
    }, [
        updateScrollableAreaDebounced,
        itemWidth,
        pageCount,
        pageIndex,
        rootRef,
        scrollableAreaRef,
        scrollSnapAlign,
    ]);
    const onScrollInternal = () => {
        const { current } = scrollableAreaRef;
        const scrollLeft = (current === null || current === void 0 ? void 0 : current.scrollLeft) || 0;
        const cursor = {
            start: scrollLeft,
            center: scrollLeft + clientWidth / 2,
            end: scrollLeft + clientWidth,
        }[scrollSnapAlign];
        const closestPoint = snapThresholds.reduce((acc, point) => (cursor > point ? point : acc), 0);
        const closestPageIndex = snapThresholds.indexOf(closestPoint);
        updateScrollableAreaDebounced();
        if (onScroll && currentPageIndex !== closestPageIndex) {
            onScroll(closestPageIndex);
        }
        setCurrentPageIndex(closestPageIndex);
    };
    return (isolateComponent("div", { css: Styles.root(), ref: rootRef },
        isolateComponent("div", { css: Styles.scrollableArea(), onScroll: onScrollInternal, ref: scrollableAreaRef, tabIndex: 0 },
            isolateComponent("div", { css: Styles.scrollingSurface({ itemWidth, gap }, pageCount, alignment) }, childArray.map((child, i) => {
                return (isolateComponent("span", { css: Styles.snappable({ scrollSnapAlign, itemWidth }), key: `${i}-snappable` }, child));
            })))));
};
export { ScrollSnap, getSnapPoints, getSnapThresholds };
//# sourceMappingURL=ScrollSnap.js.map
// @zapier/design-system@6.28.0 (14d5b99c8)
